import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useEffect, useMemo, useState } from 'react';

import {
  MIX_DELIVERY_STATUS,
  MIX_DELIVERY_STATUS_LABELS,
} from '../types/mix-deliveries.types';

export default function MixStatusChangeDialog({
  open,
  currentStatus,
  targetStatus,
  loading,
  onClose,
  onConfirm,
}) {
  const [comment, setComment] = useState('');

  const [validationError, setValidationError] = useState(false);

  const commentRequired =
    currentStatus === MIX_DELIVERY_STATUS.PREPARED &&
    targetStatus === MIX_DELIVERY_STATUS.TO_PREPARE;

  const title = useMemo(() => {
    if (!targetStatus) {
      return 'Zmiana statusu';
    }

    return `Zmień status na: ${
      MIX_DELIVERY_STATUS_LABELS[targetStatus] ?? targetStatus
    }`;
  }, [targetStatus]);

  useEffect(() => {
    if (open) {
      setComment('');
      setValidationError(false);
    }
  }, [open, targetStatus]);

  const handleConfirm = () => {
    const normalizedComment = comment.trim();

    if (commentRequired && !normalizedComment) {
      setValidationError(true);
      return;
    }

    onConfirm?.(normalizedComment || undefined);
  };

  const handleClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {commentRequired && (
            <Alert severity="warning">
              Ponowne otwarcie przygotowanego miksu wymaga podania powodu.
            </Alert>
          )}

          <Typography variant="body2">
            Aktualny status:{' '}
            <strong>
              {MIX_DELIVERY_STATUS_LABELS[currentStatus] ?? currentStatus}
            </strong>
          </Typography>

          <TextField
            label={commentRequired ? 'Powód zmiany statusu' : 'Komentarz'}
            placeholder={
              commentRequired
                ? 'Opisz, dlaczego miks wymaga ponownego przygotowania'
                : 'Opcjonalny komentarz'
            }
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);

              if (event.target.value.trim()) {
                setValidationError(false);
              }
            }}
            required={commentRequired}
            error={validationError}
            helperText={
              validationError
                ? 'Komentarz jest wymagany.'
                : commentRequired
                ? 'Komentarz zostanie zapisany w historii dostawy.'
                : 'Pole opcjonalne.'
            }
            multiline
            minRows={3}
            disabled={loading}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Anuluj
        </Button>

        <Button
          variant="contained"
          color={
            targetStatus === MIX_DELIVERY_STATUS.PREPARED
              ? 'success'
              : 'primary'
          }
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Zapisz status'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
